import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Calendar,
  User,
  Phone,
  Hash,
  Users,
  Stethoscope,
  CalendarDays,
  MapPin,
  ArrowRight,
  MessageCircle,
  Minus,
  Plus,
} from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { SERVICES, getServiceById } from "../data/services";
import { buildBookingWhatsAppUrl } from "../lib/whatsapp";
import { toast } from "sonner";

export const BookingCalculator = ({ presetTreatment, onConsumePreset }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    age: "",
    gender: "Male",
    doctorPreference: "Male Doctor",
    location: "",
    treatmentId: SERVICES[0].id,
    duration: 7,
  });

  useEffect(() => {
    if (presetTreatment) {
      setForm((f) => ({ ...f, treatmentId: presetTreatment }));
      onConsumePreset?.();
    }
  }, [presetTreatment, onConsumePreset]);

  const treatment = useMemo(
    () => getServiceById(form.treatmentId) || SERVICES[0],
    [form.treatmentId],
  );
  const total = treatment.price * form.duration;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const adjustDuration = (delta) => {
    set("duration", Math.min(30, Math.max(1, Number(form.duration) + delta)));
  };

  const validate = () => {
    if (!form.name.trim()) return "Please enter the patient name.";
    if (!/^\d{10}$/.test(form.phone.trim()))
      return "Please enter a valid 10-digit mobile number.";
    const age = Number(form.age);
    if (!age || age < 1 || age > 120) return "Please enter a valid age.";
    if (!form.location.trim()) return "Please enter your location/address.";
    return null;
  };

  const handleBook = () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const url = buildBookingWhatsAppUrl({
      name: form.name.trim(),
      phone: form.phone.trim(),
      age: form.age,
      gender: form.gender,
      doctorPreference: form.doctorPreference,
      location: form.location.trim(),
      treatmentName: treatment.name,
      duration: form.duration,
      pricePerDay: treatment.price,
      total,
    });
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp with your booking…");
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="py-20 sm:py-28 lg:py-32 bg-gradient-to-b from-slate-50/60 to-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-3xl">
          <p className="text-xs sm:text-sm text-brand-600 font-semibold tracking-[0.18em] uppercase">
            Book Your Treatment
          </p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
            See your <em className="not-italic bg-gradient-to-r from-brand-400 to-accent-400 bg-clip-text text-transparent">total instantly</em>. Confirm on WhatsApp.
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Choose your treatment and duration — your total updates in real
            time, just like a hotel booking.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="mt-10 sm:mt-14 grid lg:grid-cols-12 gap-6 lg:gap-8"
        >
          {/* Form panel */}
          <div className="lg:col-span-7 bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 sm:p-8">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field icon={User} label="Patient Name">
                <Input
                  data-testid="booking-name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. Rahul Sharma"
                  className="h-12 rounded-xl border-slate-200"
                />
              </Field>
              <Field icon={Phone} label="Mobile Number">
                <Input
                  data-testid="booking-phone"
                  value={form.phone}
                  onChange={(e) =>
                    set("phone", e.target.value.replace(/[^\d]/g, "").slice(0, 10))
                  }
                  inputMode="numeric"
                  placeholder="10-digit number"
                  className="h-12 rounded-xl border-slate-200"
                />
              </Field>
              <Field icon={Hash} label="Age">
                <Input
                  data-testid="booking-age"
                  value={form.age}
                  onChange={(e) =>
                    set("age", e.target.value.replace(/[^\d]/g, "").slice(0, 3))
                  }
                  inputMode="numeric"
                  placeholder="e.g. 38"
                  className="h-12 rounded-xl border-slate-200"
                />
              </Field>
              <Field icon={Users} label="Gender">
                <SegmentedToggle
                  testid="booking-gender"
                  options={["Male", "Female"]}
                  value={form.gender}
                  onChange={(v) => set("gender", v)}
                />
              </Field>
              <Field icon={Stethoscope} label="Doctor Preference">
                <SegmentedToggle
                  testid="booking-doctor-pref"
                  options={["Male Doctor", "Female Doctor"]}
                  value={form.doctorPreference}
                  onChange={(v) => set("doctorPreference", v)}
                />
              </Field>
              <Field icon={MapPin} label="Location / Address">
                <Input
                  data-testid="booking-location"
                  value={form.location}
                  onChange={(e) => set("location", e.target.value)}
                  placeholder="e.g. Sector 12, Noida"
                  className="h-12 rounded-xl border-slate-200"
                />
              </Field>
              <Field icon={Calendar} label="Treatment">
                <Select
                  value={form.treatmentId}
                  onValueChange={(v) => set("treatmentId", v)}
                >
                  <SelectTrigger
                    data-testid="booking-treatment-select"
                    className="h-12 rounded-xl border-slate-200"
                  >
                    <SelectValue placeholder="Choose treatment" />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl">
                    {SERVICES.map((s) => (
                      <SelectItem
                        key={s.id}
                        value={s.id}
                        data-testid={`booking-treatment-option-${s.id}`}
                      >
                        {s.name} · ₹{s.price}/day
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="sm:col-span-2">
                <Field icon={CalendarDays} label="Duration (Days)">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => adjustDuration(-1)}
                      data-testid="booking-duration-minus"
                      className="w-12 h-12 rounded-xl border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-50"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <Select
                      value={String(form.duration)}
                      onValueChange={(v) => set("duration", Number(v))}
                    >
                      <SelectTrigger
                        data-testid="booking-duration-select"
                        className="h-12 rounded-xl border-slate-200 flex-1"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-2xl max-h-72">
                        {Array.from({ length: 30 }, (_, i) => i + 1).map((d) => (
                          <SelectItem key={d} value={String(d)}>
                            {d} day{d > 1 ? "s" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() => adjustDuration(1)}
                      data-testid="booking-duration-plus"
                      className="w-12 h-12 rounded-xl border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-50"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </Field>
              </div>
            </div>
          </div>

          {/* Summary card */}
          <div className="lg:col-span-5">
            <div
              data-testid="booking-summary"
              className="lg:sticky lg:top-28 bg-slate-900 text-white rounded-3xl p-7 sm:p-8 shadow-[0_24px_60px_rgba(44,62,80,0.15)] relative overflow-hidden"
            >
              <div className="absolute -top-16 -right-16 w-60 h-60 rounded-full bg-brand-500/20 blur-3xl" />
              <div className="absolute -bottom-20 -left-10 w-60 h-60 rounded-full bg-accent-500/20 blur-3xl" />

              <p className="relative text-xs text-brand-300 font-semibold tracking-[0.18em] uppercase">
                Booking Summary
              </p>
              <h3 className="relative font-display text-2xl font-semibold mt-2">
                {treatment.name}
              </h3>

              <div className="relative mt-6 space-y-3 text-sm">
                <SummaryRow label="Price Per Day" value={`₹${treatment.price}`} />
                <SummaryRow
                  label="Duration"
                  value={`${form.duration} day${form.duration > 1 ? "s" : ""}`}
                />
                <SummaryRow label="Location" value={form.location || "-"} />
                <SummaryRow label="Doctor Preference" value={form.doctorPreference} />
                <SummaryRow label="Gender" value={form.gender} />
              </div>

              <div className="relative mt-6 pt-6 border-t border-white/15 flex items-end justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-white/60">
                    Total Amount
                  </p>
                  <p
                    data-testid="booking-total"
                    className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-1"
                  >
                    ₹{total.toLocaleString("en-IN")}
                  </p>
                </div>
                <p className="text-xs text-white/50 pb-1.5">incl. home visit</p>
              </div>

              <Button
                data-testid="booking-whatsapp-btn"
                onClick={handleBook}
                className="relative mt-7 w-full rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white h-12 px-6 text-base font-semibold shadow-lg shadow-brand-500/20"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Book on WhatsApp
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="relative mt-3 text-center text-xs text-white/55">
                You'll be redirected to WhatsApp with details prefilled.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Field = ({ icon: Icon, label, children }) => (
  <div>
    <Label className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500 flex items-center gap-1.5 mb-2">
      {Icon && <Icon className="w-3.5 h-3.5 text-brand-600" strokeWidth={1.8} />}
      {label}
    </Label>
    {children}
  </div>
);

const SegmentedToggle = ({ options, value, onChange, testid }) => (
  <div
    data-testid={testid}
    className="grid grid-cols-2 gap-1.5 p-1 rounded-xl bg-slate-100 border border-slate-200"
  >
    {options.map((opt) => (
      <button
        type="button"
        key={opt}
        data-testid={`${testid}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
        onClick={() => onChange(opt)}
        className={`h-10 rounded-lg text-sm font-medium transition-all ${
          value === opt
            ? "bg-white text-slate-900 shadow-sm"
            : "text-slate-600 hover:text-slate-800"
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-white/65">{label}</span>
    <span className="font-medium text-white">{value}</span>
  </div>
);
